import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import type { Nullable } from "@core/domain/types/nullable.type";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import { BaseViewModel } from "@iot-platform/core";
import type {
    NotebookMessage,
    NotebookMessageFileCreate,
} from "@notebook/domain/models/notebook-message.model";
import { NotebookMessageFileCreateUseCase } from "@notebook/domain/usecases/notebook-message-file-create.usecase";
import { NotebookMessageFileDeleteUseCase } from "@notebook/domain/usecases/notebook-message-file-delete.usecase";
import { NotebookMessageGetByIdUseCase } from "@notebook/domain/usecases/notebook-message-get-by-id.usecase";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class MessageModalFormViewmodel extends BaseViewModel {
    @observable
    notebookMessage: Nullable<Readonly<NotebookMessage>> = null;

    @observable
    notebookId: Nullable<string> = null;

    constructor(
        @inject(NotebookMessageFileCreateUseCase)
        private readonly notebookMessageFileCreateUseCase: NotebookMessageFileCreateUseCase,
        @inject(NotebookMessageFileDeleteUseCase)
        private readonly notebookMessageFileDeleteUseCase: NotebookMessageFileDeleteUseCase,
        @inject(NotebookMessageGetByIdUseCase)
        private readonly notebookMessageGetByIdUseCase: NotebookMessageGetByIdUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    async fetchNotebookMessage(): Promise<void> {
        if (!this.notebookId || !this.notebookMessage)
            throw new Error("notebookId or notebookMessageId is missing");

        const notebookMessage =
            await this.notebookMessageGetByIdUseCase.execute(
                this.notebookMessage.id.value,
                this.notebookId,
            );

        runInAction(() => {
            this.notebookMessage = notebookMessage;
        });
    }

    @action
    async addMessageFile(
        notebookId: string,
        notebookMessageId: string,
        files: File[],
    ): Promise<void> {
        LoadLayoutStore.start();

        const fileData: NotebookMessageFileCreate = {
            files: files,
        };

        const createdFile = await this.notebookMessageFileCreateUseCase.execute(
            notebookId,
            notebookMessageId,
            fileData,
        );

        if (createdFile) {
            await this.reloadNotebookMessage();
            ToastManagerRepository.success();
        }

        LoadLayoutStore.finish();
    }

    @action
    async deleteMessageFile(
        notebookId: string,
        notebookMessageId: string,
        notebookMessageFileId: string,
    ): Promise<void> {
        LoadLayoutStore.start();

        const deletedFile = await this.notebookMessageFileDeleteUseCase.execute(
            notebookId,
            notebookMessageId,
            notebookMessageFileId,
        );

        if (deletedFile) {
            ToastManagerRepository.success();
            await this.reloadNotebookMessage();
        }

        LoadLayoutStore.finish();
    }

    @action
    setNotebookMessage(notebookMessage: Readonly<NotebookMessage>): void {
        runInAction(() => {
            this.notebookMessage = notebookMessage;
        });
    }

    @action
    setNotebookId(notebookId: string): void {
        runInAction(() => {
            this.notebookId = notebookId;
        });
    }

    async reloadNotebookMessage(): Promise<void> {
        LoadLayoutStore.start();
        await this.fetchNotebookMessage();
        LoadLayoutStore.finish();
    }
}
